import { AttributeKindEnum } from "@brewbase/db/types";
import { getDisplaySymbol } from "@brewbase/lib";
import { cn } from "@/lib/utils";
import { NumericInput } from "@/components/ui/numeric-input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import UnitSelect from "@/components/unit-select";
import {
  useCpSettings,
  type AdvancedToleranceType,
  type SimpleLimitType,
} from "@/lib/use-cp-settings";

/** Neutral acceptance-window shape. Both call sites map their own columns
 *  (template `cp_default_number_*`, protocol override `number_*`) to/from this. */
export interface CpWindow {
  target: string | null;
  absLower: string | null;
  absUpper: string | null;
  relLower: string | null;
  relUpper: string | null;
}

const toNum = (s: string | null | undefined): number | null => {
  if (s === "" || s === null || s === undefined) return null;
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
};
const fromNum = (n: number | null): string => (n === null ? "" : String(n));
// numeric columns are typed string|null but arrive as numbers over JSON.
const asStr = (v: string | number | null | undefined): string | null =>
  v === null || v === undefined || v === "" ? null : String(v);

export interface CpSpecEditorProps {
  kind: AttributeKindEnum;
  window: CpWindow;
  unit: string | null;
  checkValue: boolean | null;
  onWindowChange: (w: CpWindow) => void;
  onUnitChange: (unit: string | null) => void;
  onCheckChange: (v: boolean) => void;
  disabled?: boolean;
  /** Unique prefix for radio/label element ids. */
  idPrefix: string;
}

/**
 * Shared editor for a control point's acceptance spec — the numeric window
 * (target + absolute/relative tolerance, symmetric/asymmetric, or one-sided
 * limits) or the boolean pass/fail expectation. Used by both the template
 * inspector (authoring `cp_default_*`) and the protocol panel (per-operation
 * overrides), so the CP spec is authored identically everywhere.
 */
export function CpSpecEditor({
  kind,
  window,
  unit,
  checkValue,
  onWindowChange,
  onUnitChange,
  onCheckChange,
  disabled,
  idPrefix,
}: CpSpecEditorProps) {
  if (kind === AttributeKindEnum.CHECK) {
    return (
      <CheckSpec value={checkValue} onChange={onCheckChange} disabled={disabled} />
    );
  }
  return (
    <NumericSpec
      window={window}
      unit={unit}
      onWindowChange={onWindowChange}
      onUnitChange={onUnitChange}
      disabled={disabled}
      idPrefix={idPrefix}
    />
  );
}

function NumericSpec({
  window,
  unit,
  onWindowChange,
  onUnitChange,
  disabled,
  idPrefix,
}: {
  window: CpWindow;
  unit: string | null;
  onWindowChange: (w: CpWindow) => void;
  onUnitChange: (unit: string | null) => void;
  disabled?: boolean;
  idPrefix: string;
}) {
  const cpSettings = useCpSettings({
    currentValues: {
      cp_default_number_target: asStr(window.target),
      cp_default_number_abs_tol_lower: asStr(window.absLower),
      cp_default_number_abs_tol_upper: asStr(window.absUpper),
      cp_default_number_rel_tol_lower: asStr(window.relLower),
      cp_default_number_rel_tol_upper: asStr(window.relUpper),
    },
    onChange: (v) =>
      onWindowChange({
        target: asStr(v.cp_default_number_target),
        absLower: asStr(v.cp_default_number_abs_tol_lower),
        absUpper: asStr(v.cp_default_number_abs_tol_upper),
        relLower: asStr(v.cp_default_number_rel_tol_lower),
        relUpper: asStr(v.cp_default_number_rel_tol_upper),
      }),
  });

  const unitEl = (
    <UnitSelect
      value={unit ?? ""}
      onChange={(u) => onUnitChange(u || null)}
      placeholder="Unit"
      className="max-w-max"
    />
  );

  return (
    <div className="space-y-2">
      <RadioGroup
        value={cpSettings.mode}
        onValueChange={(v) =>
          cpSettings.setMode(v as "simple-limits" | "target-tolerance" | "custom")
        }
        className="flex items-center gap-4"
        disabled={disabled}
      >
        {[
          { value: "simple-limits", id: "limits", label: "Limits" },
          { value: "target-tolerance", id: "tol", label: "Target ± Tolerance" },
          { value: "custom", id: "custom", label: "Custom" },
        ].map((m) => (
          <div key={m.value} className="flex items-center gap-1">
            <RadioGroupItem
              value={m.value}
              id={`${idPrefix}-${m.id}`}
              className="h-3 w-3"
            />
            <Label
              htmlFor={`${idPrefix}-${m.id}`}
              className="text-xs font-normal cursor-pointer"
            >
              {m.label}
            </Label>
          </div>
        ))}
      </RadioGroup>

      {cpSettings.mode === "simple-limits" && (
        <div className="flex items-center gap-2 flex-wrap">
          <Select
            value={cpSettings.simpleLimits.limitType}
            onValueChange={(v) =>
              cpSettings.updateSimpleLimits({ limitType: v as SimpleLimitType })
            }
            disabled={disabled}
          >
            <SelectTrigger className="w-24 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="minimum">Min</SelectItem>
              <SelectItem value="maximum">Max</SelectItem>
              <SelectItem value="range">Range</SelectItem>
            </SelectContent>
          </Select>
          {cpSettings.simpleLimits.limitType !== "maximum" && (
            <NumericInput
              value={toNum(cpSettings.simpleLimits.minValue)}
              onValueChange={(n) =>
                cpSettings.updateSimpleLimits({ minValue: fromNum(n) })
              }
              placeholder="Min"
              className="w-24 h-8"
              disabled={disabled}
            />
          )}
          {cpSettings.simpleLimits.limitType !== "minimum" && (
            <NumericInput
              value={toNum(cpSettings.simpleLimits.maxValue)}
              onValueChange={(n) =>
                cpSettings.updateSimpleLimits({ maxValue: fromNum(n) })
              }
              placeholder="Max"
              className="w-24 h-8"
              disabled={disabled}
            />
          )}
          {unitEl}
        </div>
      )}

      {cpSettings.mode === "target-tolerance" && (
        <div className="flex items-center gap-2 flex-wrap">
          <NumericInput
            value={toNum(cpSettings.targetTolerance.target)}
            onValueChange={(n) =>
              cpSettings.updateTargetTolerance({ target: fromNum(n) })
            }
            placeholder="Target"
            className="w-24 h-8"
            disabled={disabled}
          />
          {unitEl}
          <span className="text-sm text-muted-foreground">±</span>
          <NumericInput
            value={toNum(cpSettings.targetTolerance.tolerance)}
            onValueChange={(n) =>
              cpSettings.updateTargetTolerance({ tolerance: fromNum(n) })
            }
            placeholder="Tol"
            className="w-20 h-8"
            disabled={disabled}
          />
          <ToleranceTypeSelect
            value={cpSettings.targetTolerance.toleranceType}
            unit={unit}
            disabled={disabled}
            onChange={(t) => cpSettings.updateTargetTolerance({ toleranceType: t })}
          />
        </div>
      )}

      {cpSettings.mode === "custom" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <NumericInput
              value={toNum(cpSettings.advanced.target)}
              onValueChange={(n) =>
                cpSettings.updateAdvanced({ target: fromNum(n) })
              }
              placeholder="Target"
              className="w-24 h-8"
              disabled={disabled}
            />
            {unitEl}
            <ToleranceTypeSelect
              value={cpSettings.advanced.toleranceType}
              unit={unit}
              disabled={disabled}
              onChange={(t) => cpSettings.updateAdvanced({ toleranceType: t })}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-olive-9 w-10">Lower</span>
            <NumericInput
              value={toNum(
                cpSettings.advanced.toleranceType === "absolute"
                  ? cpSettings.advanced.absLower
                  : cpSettings.advanced.relLower,
              )}
              onValueChange={(n) =>
                cpSettings.advanced.toleranceType === "absolute"
                  ? cpSettings.updateAdvanced({ absLower: fromNum(n) })
                  : cpSettings.updateAdvanced({ relLower: fromNum(n) })
              }
              placeholder="−"
              className="w-24 h-8"
              disabled={disabled}
            />
            <span className="text-xs text-olive-9 w-10 text-right">Upper</span>
            <NumericInput
              value={toNum(
                cpSettings.advanced.toleranceType === "absolute"
                  ? cpSettings.advanced.absUpper
                  : cpSettings.advanced.relUpper,
              )}
              onValueChange={(n) =>
                cpSettings.advanced.toleranceType === "absolute"
                  ? cpSettings.updateAdvanced({ absUpper: fromNum(n) })
                  : cpSettings.updateAdvanced({ relUpper: fromNum(n) })
              }
              placeholder="+"
              className="w-24 h-8"
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ToleranceTypeSelect({
  value,
  unit,
  disabled,
  onChange,
}: {
  value: AdvancedToleranceType;
  unit: string | null;
  disabled?: boolean;
  onChange: (t: AdvancedToleranceType) => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(v) => onChange(v as AdvancedToleranceType)}
      disabled={disabled}
    >
      <SelectTrigger className="w-20 h-8">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="absolute">{getDisplaySymbol(unit ?? "") || "abs"}</SelectItem>
        <SelectItem value="relative">%</SelectItem>
      </SelectContent>
    </Select>
  );
}

function CheckSpec({
  value,
  onChange,
  disabled,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-olive-9 mr-1">Acceptance</span>
      {[
        { v: true, label: "Must pass" },
        { v: false, label: "Must fail" },
      ].map((opt) => (
        <button
          key={String(opt.v)}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.v)}
          className={cn(
            "px-2 py-1 text-xs border transition-colors disabled:opacity-50",
            value === opt.v
              ? "border-[var(--farmless-green)] bg-farmless-green/10 text-foreground"
              : "border-olive-6 text-olive-9 hover:text-foreground hover:border-olive-8",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
